import {
  collection,
  query,
  where,
  limit,
  orderBy,
  onSnapshot,
  getDocs,
  doc,
  getDoc,
  documentId,
  getCountFromServer,
} from "firebase/firestore";
import { db } from "../../../fb";
import { checkIsValidAddress } from "@/store/utils/ordersUtils";
import { dbCollection, orderFilterCategories, orderStatus } from "@/constants";
import { appSettings } from "@/config";

const defaultState = {
  loadingOrders: false,
  orders: [],
  ordersToFulfill: [],
  /** Erst-Snapshot der Versandliste komplett verarbeitet (inkl. Tasks) */
  ordersToFulfillReady: false,
  /** Lade-Limit der Versandliste (neueste zuerst — abgeschnitten werden die ÄLTESTEN); „Alle laden" hebt es auf */
  ordersToFulfillLimit: 1500,
  ordersToFulfillUnlimited: false,
  /** Gesamtzahl READY_FOR_FULFILL laut Count-Abfrage (nur gepflegt, wenn das Limit erreicht ist) */
  ordersToFulfillTotal: null,
  ordersInFulfill: [],
  ordersWithError: [],
  filter: {
    orderId: "",
    email: "",
    quickSearch: "",
    filterCategory: orderFilterCategories.ALL,
  },
  unsubOrdersToFulfill: () => {},
  unsubOrdersInFulfill: () => {},
  unsubOrdersWithError: () => {},
};

const getters = {
  ordersToFulfill: (state) => state.ordersToFulfill,
  ordersToFulfillReady: (state) => state.ordersToFulfillReady,
  ordersToFulfillLimit: (state) => state.ordersToFulfillLimit,
  ordersToFulfillTotal: (state) => state.ordersToFulfillTotal,
  ordersToFulfillUnlimited: (state) => state.ordersToFulfillUnlimited,
  /** Liste abgeschnitten: mehr offene Bestellungen als geladen (Count-Abfrage > Limit) */
  ordersToFulfillTruncated: (state) =>
    !state.ordersToFulfillUnlimited && state.ordersToFulfillTotal != null && state.ordersToFulfillTotal > state.ordersToFulfillLimit,
  ordersInFulfill: (state) => state.ordersInFulfill,
  ordersWithError: (state) => state.ordersWithError,
  loadingOrders: (state) => state.loadingOrders,
  ordersPerPage: (state) => state.ordersPerPage,
  currentOrdersPage: (state) => state.currentOrdersPage,
  ordersSortBy: (state) => state.filter.sortBy,
  ordersSortDesc: (state) => state.filter.sortDesc,
  filterOrderId: (state) => state.filter.orderId,
  filterEmail: (state) => state.filter.email,
  filterQuickSearch: (state) => state.filter.quickSearch,
  filterCategory: (state) => state.filter.filterCategory,
  orders: (state) => state.orders,
};

/** Einmal gemerkt: Sammelabfrage auf tasks verboten (Rolle ohne ADMIN/MANAGE_TASKS — die Regel readWriteTasks
 *  prüft resource.data und gilt damit nur für Einzel-Reads) → dauerhaft Einzel-getDoc wie früher */
let taskBatchDenied = false;

/** Tasks gebündelt laden: ID-in-Abfragen à 10 (SDK-9-Grenze) parallel → { taskUid: Task }; Rückfall auf Einzel-Reads */
async function fetchTasksByIds(ids) {
  const unique = [...new Set(ids.filter(Boolean).map(String))];
  const map = {};
  const chunks = [];
  for (let i = 0; i < unique.length; i += 10) chunks.push(unique.slice(i, i + 10));
  const single = async (chunk) => {
    await Promise.all(
      chunk.map(async (id) => {
        const snap = await getDoc(doc(db, dbCollection.TASKS, id));
        if (snap.exists()) map[id] = snap.data();
      })
    );
  };
  await Promise.all(
    chunks.map(async (chunk) => {
      if (taskBatchDenied) return single(chunk);
      try {
        const snap = await getDocs(query(collection(db, dbCollection.TASKS), where(documentId(), "in", chunk)));
        snap.docs.forEach((d) => (map[d.id] = d.data()));
      } catch (e) {
        if (e && e.code === "permission-denied") {
          taskBatchDenied = true;
          return single(chunk);
        }
        throw e;
      }
    })
  );
  return map;
}

/** Bestellung + volle Tasks (fehlende Task-Dokumente werden wie bisher übersprungen) + Adress-Warnung */
const withFullTasks = (order, taskMap) => ({
  ...order,
  showOrderWarning: !checkIsValidAddress(order),
  tasks: (order.tasks || []).filter((t) => taskMap[String(t.taskUid)]).map((t) => ({ ...t, ...taskMap[String(t.taskUid)] })),
});

const actions = {
  async loadOrders({ dispatch }) {
    dispatch("loadOrdersToFulfill");
    dispatch("loadOrdersInFulfill");
    dispatch("loadOrdersWithError");
  },
  async loadAllOrders({ commit, state }) {
    commit("setLoadingOrders", true);
    commit("setOrders", {
      orders: [],
      stateFieldName: "orders",
    });

    const ordersCollection = collection(db, "orders");
    let ordersQuery = query(
      ordersCollection,
      orderBy("orderDate", "desc"),
      limit(50)
    );

    const { orderId, email, filterCategory } = state.filter;

    if (filterCategory === orderFilterCategories.DELAYED_FULFILL) {
      const threeDaysAgo = new Date();
      threeDaysAgo.setDate(
        threeDaysAgo.getDate() - appSettings.MAX_DAYS_UNTIL_FULFILL
      );

      ordersQuery = query(
        ordersCollection,
        where("orderDate", "<", threeDaysAgo.toISOString()),
        where("status", "in", [
          orderStatus.IN_PROGRESS,
          orderStatus.ORDER_CREATED,
        ]),
        limit(100)
      );
    }

    if (filterCategory === orderFilterCategories.DELAYED_SHIPMENT) {
      const threeDaysAgo = new Date();
      threeDaysAgo.setDate(
        threeDaysAgo.getDate() - appSettings.MAX_DAYS_UNTIL_SHIPMENT
      );

      ordersQuery = query(
        ordersCollection,
        where("fulfillDate", "<", threeDaysAgo.toISOString()),
        where("status", "==", orderStatus.FULFILLED),
        orderBy("fulfillDate", "desc"),
        limit(500)
      );
    }

    if (orderId) {
      ordersQuery = query(
        ordersCollection,
        orderBy("orderName", "desc"),
        where("orderName", ">=", orderId),
        where("orderName", "<=", orderId + "~"),
        limit(50)
      );
    }

    if (email) {
      ordersQuery = query(ordersQuery, where("customer.email", "==", email));
    }

    const querySnapshot = await getDocs(ordersQuery);
    const orders = querySnapshot.docs.map((doc) => doc.data());

    commit("setOrders", {
      orders: orders,
      stateFieldName: "orders",
    });
    commit("setLoadingOrders", false);
  },
  /**
   * Versandliste live laden. Tasks werden gebündelt per ID-in-Abfrage geholt (statt ein getDoc je Task),
   * Snapshots werden nacheinander verarbeitet (keine Überholer zwischen Erst-Snapshot und Änderungen).
   * Limit 1500 (02.11.2026, vorher 600 — bei 636 offenen fehlten die 36 ältesten = Holds/Adressfälle);
   * ist es erreicht, zählt eine Count-Abfrage die Gesamtmenge → Leiste „Liste abgeschnitten" + „Alle laden" ({ unlimited: true }).
   */
  async loadOrdersToFulfill({ commit, state }, { unlimited = state.ordersToFulfillUnlimited } = {}) {
    const active = state.unsubOrdersToFulfill && state.unsubOrdersToFulfill.__active;
    if (active && !!unlimited === !!state.ordersToFulfillUnlimited) {
      return;
    }

    commit("setLoadingOrders", true);
    commit("setOrders", {
      orders: [],
      stateFieldName: "ordersToFulfill",
    });
    commit("setOrdersToFulfillUnlimited", !!unlimited);

    const ordersCollection = collection(db, "orders");
    const baseQuery = query(
      ordersCollection,
      where("status", "==", orderStatus.READY_FOR_FULFILL),
      orderBy("orderDate", "desc")
    );
    const limitN = state.ordersToFulfillLimit;
    const ordersQuery = unlimited ? baseQuery : query(baseQuery, limit(limitN));

    if (typeof state.unsubOrdersToFulfill === "function") {
      state.unsubOrdersToFulfill.__active = false;
      state.unsubOrdersToFulfill();
    }
    commit("setOrdersToFulfillReady", false);

    // Gesamtzahl nur abfragen, wenn das Limit voll ist (1 Lesevorgang je 1000 Treffer), höchstens alle 30 s
    let lastCountAt = 0;
    const refreshTotal = async (size) => {
      if (unlimited || size < limitN) {
        commit("setOrdersToFulfillTotal", size);
        return;
      }
      if (Date.now() - lastCountAt < 30000) return;
      lastCountAt = Date.now();
      try {
        const countQuery = query(ordersCollection, where("status", "==", orderStatus.READY_FOR_FULFILL));
        const res = await getCountFromServer(countQuery);
        commit("setOrdersToFulfillTotal", res.data().count);
      } catch (e) {
        console.warn("Versandliste: Gesamtzahl nicht abfragbar", e);
      }
    };

    let firstSnapshot = true;
    let chain = Promise.resolve();
    const handleSnapshot = async (snapshot) => {
      const isInitial = firstSnapshot;
      firstSnapshot = false;
      const changes = snapshot.docChanges();
      // Tasks aller geänderten Bestellungen in einem Rutsch (removed braucht keine Tasks)
      const taskIds = [];
      for (const change of changes) {
        if (change.type === "removed") continue;
        for (const t of change.doc.data().tasks || []) taskIds.push(t.taskUid);
      }
      const taskMap = await fetchTasksByIds(taskIds);
      for (const change of changes) {
        const changedOrder = change.doc.data();
        if (change.type === "removed") {
          commit("deleteOrder", { order: changedOrder, stateFieldName: "ordersToFulfill" });
          continue;
        }
        const orderData = withFullTasks(changedOrder, taskMap);
        commit(change.type === "added" ? "addOrder" : "updateOrder", {
          order: orderData,
          stateFieldName: "ordersToFulfill",
        });
      }
      if (isInitial) {
        commit("setOrdersToFulfillReady", true);
        commit("setLoadingOrders", false);
      }
      refreshTotal(snapshot.size);
    };
    const unsub = onSnapshot(ordersQuery, (snapshot) => {
      chain = chain.then(() => handleSnapshot(snapshot)).catch((e) => console.error("Versandliste-Snapshot", e));
    });
    unsub.__active = true;
    state.unsubOrdersToFulfill = unsub;
  },
  async loadOrdersInFulfill({ commit, state }) {
    if (state.unsubOrdersInFulfill && state.unsubOrdersInFulfill.__active) {
      return;
    }

    commit("setLoadingOrders", true);
    commit("setOrders", {
      orders: [],
      stateFieldName: "ordersInFulfill",
    });

    const ordersCollection = collection(db, "orders");
    const ordersQuery = query(
      ordersCollection,
      where("status", "in", [
        orderStatus.WAITING_FOR_FULFILL,
        orderStatus.IN_FULFILL,
      ]),
      orderBy("orderDate", "desc"),
      limit(250)
    );

    if (typeof state.unsubOrdersInFulfill === "function") {
      state.unsubOrdersInFulfill();
    }
    const unsub = onSnapshot(ordersQuery, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        const changedOrder = change.doc.data();
        const showOrderWarning = !checkIsValidAddress(changedOrder);
        const orderData = {
          ...changedOrder,
          showOrderWarning,
        };
        if (change.type === "added") {
          commit("addOrder", {
            order: orderData,
            stateFieldName: "ordersInFulfill",
          });
        } else if (change.type === "modified") {
          commit("updateOrder", {
            order: orderData,
            stateFieldName: "ordersInFulfill",
          });
        } else if (change.type === "removed") {
          commit("deleteOrder", {
            order: orderData,
            stateFieldName: "ordersInFulfill",
          });
        }
      });
    });
    unsub.__active = true;
    state.unsubOrdersInFulfill = unsub;

    commit("setLoadingOrders", false);
  },
  async loadOrdersWithError({ commit, state }) {
    if (state.unsubOrdersWithError && state.unsubOrdersWithError.__active) {
      return;
    }

    commit("setLoadingOrders", true);
    commit("setOrders", {
      orders: [],
      stateFieldName: "ordersWithError",
    });

    const ordersCollection = collection(db, "orders");
    const ordersQuery = query(
      ordersCollection,
      where("status", "==", orderStatus.FULFILL_ERROR),
      orderBy("orderDate", "desc"),
      limit(100)
    );

    if (typeof state.unsubOrdersWithError === "function") {
      state.unsubOrdersWithError();
    }
    const unsub = onSnapshot(ordersQuery, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        const changedOrder = change.doc.data();
        const showOrderWarning = !checkIsValidAddress(changedOrder);
        const orderData = {
          ...changedOrder,
          showOrderWarning,
        };
        if (change.type === "added") {
          commit("addOrder", {
            order: orderData,
            stateFieldName: "ordersWithError",
          });
        } else if (change.type === "modified") {
          commit("updateOrder", {
            order: orderData,
            stateFieldName: "ordersWithError",
          });
        } else if (change.type === "removed") {
          commit("deleteOrder", {
            order: orderData,
            stateFieldName: "ordersWithError",
          });
        }
      });
    });
    unsub.__active = true;
    state.unsubOrdersWithError = unsub;

    commit("setLoadingOrders", false);
  },
  unsubscribeOrders({ state }) {
    ["unsubOrdersToFulfill", "unsubOrdersInFulfill", "unsubOrdersWithError"].forEach((key) => {
      if (typeof state[key] === "function") state[key]();
      state[key] = () => {};
    });
  },
};

const mutations = {
  setLoadingOrders: (state, bool) => (state.loadingOrders = bool),
  setOrdersToFulfillReady: (state, bool) => (state.ordersToFulfillReady = bool),
  setOrdersToFulfillTotal: (state, n) => (state.ordersToFulfillTotal = n),
  setOrdersToFulfillUnlimited: (state, bool) => (state.ordersToFulfillUnlimited = bool),
  setOrders: (state, { orders, stateFieldName }) =>
    (state[stateFieldName] = orders),
  addOrder(state, { order, stateFieldName }) {
    // Add the task to the state
    if (order.isExpress) {
      state[stateFieldName].unshift(order);
    } else {
      state[stateFieldName].push(order);
    }
  },
  updateOrder(state, { order, stateFieldName }) {
    // Find the index of the task in the state
    const index = state[stateFieldName].findIndex((o) => o.uid === order.uid);
    // Update the task in the state
    if (index !== -1) {
      state[stateFieldName].splice(index, 1, order);
    }
  },
  deleteOrder(state, { order, stateFieldName }) {
    // Find the index of the task in the state
    const index = state[stateFieldName].findIndex((o) => o.uid === order.uid);
    // Remove the task from the state
    if (index !== -1) {
      state[stateFieldName].splice(index, 1);
    }
  },
  setFilterOrderId: (state, orderId) => (state.filter.orderId = orderId),
  setFilterEmail: (state, email) => (state.filter.email = email),
  setFilterCategory: (state, filterCategory) =>
    (state.filter.filterCategory = filterCategory),
  setFilterQuickSearch: (state, searchTerm) =>
    (state.filter.quickSearch = searchTerm),
};

const state = Object.assign(
  {},
  defaultState,
  window.sessionStorage["taskDash"] ? JSON.parse(window.sessionStorage["taskDash"]).orders : {}
);

export default {
  state,
  getters,
  actions,
  mutations,
};
